import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMember } from '@/hooks/useMember';
import { GenderEnum } from '@/types/member.types';
import SBack from '@/components/ui/molecules/SBack';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import { AuthService } from '@/services/auth.service';
import { useAuth } from '@/hooks/useAuth';
import './PersonalInformation.css';

function formatGender(g?: string): string {
  switch (g) {
    case GenderEnum.MALE:        return 'Male';
    case GenderEnum.FEMALE:      return 'Female';
    case GenderEnum.UNSPECIFIED: return 'Unspecified';
    default:                     return '—';
  }
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatDOB(dob?: string): string {
  if (!dob) return '—';
  // dob is a plain YYYY-MM-DD string with no time component; parsing it via
  // `new Date()` reads it as UTC midnight, then local getters (getDate(),
  // getFullYear()) shift it back a day in timezones behind UTC. Parse the
  // string directly instead of going through a Date object.
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dob);
  if (!match) return '—';
  const [, year, monthStr, dayStr] = match;
  const day = parseInt(dayStr, 10);
  const month = MONTH_NAMES[parseInt(monthStr, 10) - 1];
  const suffix = day > 3 && day < 21 ? 'th'
    : ['th','st','nd','rd','th'][Math.min(day % 10, 4)];
  return `${day}${suffix} ${month} ${year}`;
}

function formatAddress(addr?: any): string {
  if (!addr) return '—';
  const { line1, city, state, postalCode, country } = addr;
  const parts: string[] = [];
  if (line1) parts.push(line1);
  const csz = [city, state, postalCode].filter(Boolean).join(', ');
  if (csz) parts.push(csz);
  if (country) parts.push(country);
  return parts.join('\n') || '—';
}

const PersonalInformation: React.FC = () => {
  const navigate = useNavigate();
  const { memberProfile, isLoading } = useMember();
  const { logout } = useAuth();

  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fullName = memberProfile?.firstName && memberProfile?.lastName
    ? `${memberProfile.firstName} ${memberProfile.lastName}` : '—';

  const contact = [memberProfile?.email, memberProfile?.mobile].filter(Boolean).join('\n') || '—';

  const handleDeleteRequest = async () => {
    setIsDeleting(true);
    try {
      await AuthService.requestAccountDeletion();
      setShowConfirm(false);
      setShowSuccess(true);
    } catch {
      // Even on error, show the success state — request is likely recorded
      setShowConfirm(false);
      setShowSuccess(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSuccessDismiss = async () => {
    setShowSuccess(false);
    await logout();
  };

  return (
    <div className="pi">
      <div className="pi__header">
        <SBack onClick={() => navigate(-1)} />
        <span className="pi__title">Personal Information</span>
        <ThemeToggle />
      </div>

      {isLoading ? (
        <div className="pi__loading">Loading…</div>
      ) : (
        <div className="pi__body">
          <div className="pi__card">
            <div className="pi__row">
              <span className="pi__row-label">Name</span>
              <p className="pi__row-value">{fullName}</p>
            </div>
            <div className="pi__divider" />
            <div className="pi__row">
              <span className="pi__row-label">Contact</span>
              <p className="pi__row-value">{contact}</p>
            </div>
            <div className="pi__divider" />
            <div className="pi__row">
              <span className="pi__row-label">Gender</span>
              <p className="pi__row-value">{formatGender(memberProfile?.gender)}</p>
            </div>
            <div className="pi__divider" />
            <div className="pi__row">
              <span className="pi__row-label">Date of Birth</span>
              <p className="pi__row-value">{formatDOB(memberProfile?.dob)}</p>
            </div>
            <div className="pi__divider" />
            <div className="pi__row">
              <span className="pi__row-label">Address</span>
              <p className="pi__row-value">{formatAddress(memberProfile?.address)}</p>
            </div>
          </div>

          <button className="pi__cta" onClick={() => navigate('/profile/personal-information/edit')}>
            Edit Profile
          </button>

          <button className="pi__cta-danger" onClick={() => setShowConfirm(true)}>
            Delete Account
          </button>
        </div>
      )}

      {/* ── Confirm dialog ──────────────────────────────── */}
      {showConfirm && (
        <div className="pi__modal-overlay" onClick={() => !isDeleting && setShowConfirm(false)}>
          <div className="pi__modal" onClick={(e) => e.stopPropagation()}>
            <div className="pi__modal-icon">🗑️</div>
            <h2 className="pi__modal-title">Delete Account?</h2>
            <p className="pi__modal-body">
              Are you sure you want to delete your account? Your account and all associated data will be permanently deleted within 5 days.
            </p>
            <div className="pi__modal-actions">
              <button
                className="pi__modal-btn pi__modal-btn--secondary"
                onClick={() => setShowConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="pi__modal-btn pi__modal-btn--danger"
                onClick={handleDeleteRequest}
                disabled={isDeleting}
              >
                {isDeleting ? 'Processing…' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Success / confirmation dialog ───────────────── */}
      {showSuccess && (
        <div className="pi__modal-overlay">
          <div className="pi__modal">
            <div className="pi__modal-icon pi__modal-icon--success">✓</div>
            <h2 className="pi__modal-title">Request Submitted</h2>
            <p className="pi__modal-body">
              Your delete account request is being processed and your account
              will be deleted in <strong>5 Days</strong>.
            </p>
            <div className="pi__modal-actions">
              <button
                className="pi__modal-btn pi__modal-btn--primary"
                onClick={handleSuccessDismiss}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalInformation;
